// Clientes das origens de dado. Um por dono, conforme docs/01-arquitetura.md.
//
// Nosso estado  → Supabase (schema `api`)
// Frota e custo → LiteLLM, pelo gateway
// Trabalho      → Linear, pelo gateway
// Código        → GitHub, pelo gateway

import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? "http://localhost:54321";
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";

/** Sem chave o app ainda abre e explica o que falta. Um clone novo não pode
 *  cair em tela branca — o primeiro contato com o projeto é este. */
export const configured = Boolean(SUPABASE_KEY);

export const db = createClient(SUPABASE_URL, SUPABASE_KEY || "sem-chave", {
  db: { schema: "api" },
  auth: { persistSession: configured },
});

/** Erro de origem. `next` diz o que fazer — a UI mostra isso, não o stack. */
export class SourceError extends Error {
  constructor(readonly source: string, message: string, readonly next: string) {
    super(message);
  }
}

/** Garante um JWT com role `authenticated` antes de qualquer leitura.
 *  Instância local sem login ainda: sessão anônima em vez de afrouxar o RLS. */
export async function ensureSession(): Promise<void> {
  if (!configured) return;
  const { data } = await db.auth.getSession();
  if (!data.session) await db.auth.signInAnonymously();
}

const naoConfigurado = (source: string) =>
  new SourceError(
    source,
    "Supabase não configurado",
    "copie web/.env.example para web/.env, rode `supabase start` e preencha VITE_SUPABASE_ANON_KEY",
  );

/** Em dev quem injeta a credencial é o proxy do Vite; em produção, a Edge
 *  Function. Nos dois casos a chave do upstream fica no servidor — o bundle
 *  nunca a vê. */
const GATEWAY = "/gw";

/** O repo do projeto. Antes cada página perguntava ao GitHub qual era o repo
 *  ANTES de pedir qualquer dado — uma ida de rede inteira (≈400 ms) para
 *  descobrir uma string que já sabemos, e nove cópias do mesmo código fazendo
 *  isso. O nome é constante; se ele estiver errado, a própria chamada de dado
 *  devolve 404 e a tela mostra o erro, que é mais honesto que adivinhar outro
 *  repositório. */
export const REPO_PADRAO = "by-mav/hope";
export const repoAtual = (): Promise<string> => Promise.resolve(REPO_PADRAO);

/** Cada upstream guarda a mensagem de erro num lugar: o PostgREST em `detail`,
 *  o LiteLLM aninhada em `error.message` — e ainda cola o traceback do Python
 *  inteiro junto. Reduz tudo a uma linha legível, senão a tela mostra
 *  "[object Object]" e o erro fica invisível. */
function motivo(body: unknown, status: number): string {
  const b = body as Record<string, any> | null;
  const bruto = b?.error?.message ?? b?.detail ?? b?.message ?? b?.error;
  const texto = typeof bruto === "string" ? bruto : bruto ? JSON.stringify(bruto) : "";
  return texto ? texto.split("\n")[0].slice(0, 200) : `HTTP ${status}`;
}

/** Cache de leitura, com revalidação em segundo plano.
 *
 *  O app parecia lento por refazer trabalho, não por rede lenta: cada troca de
 *  tela repetia as mesmas listas do zero. Aqui um GET responde na hora com o
 *  que já foi buscado e revalida atrás; só a primeira visita espera rede.
 *
 *  Três regras: leitura já vista volta instantânea; duas telas pedindo a mesma
 *  URL ao mesmo tempo fazem uma requisição só; e toda escrita limpa tudo,
 *  senão a tela mostraria o estado anterior depois de você mudar algo. */
const FRESCO_MS = 20_000;
const cache = new Map<string, { em: number; dado: unknown }>();
const emVoo = new Map<string, Promise<unknown>>();

/** Avisado quando uma revalidação traz dado diferente do que está na tela.
 *  O router assina isto para repintar sem você pedir. */
type Ouvinte = () => void;
const ouvintes = new Set<Ouvinte>();
export const aoAtualizar = (f: Ouvinte): (() => void) => {
  ouvintes.add(f);
  return () => ouvintes.delete(f);
};

/** Instante da última escrita. Logo depois de gravar, o upstream ainda está
 *  propagando: o GitHub leva alguns segundos para uma issue nova entrar na
 *  LISTAGEM, mesmo já respondendo por número. Guardar essa leitura por 20 s
 *  congelava o engano — você criava a task, o board abria sem ela, e voltar
 *  para Tasks continuava sem ela até o cache vencer. */
let ultimaEscrita = 0;
const DESCONFIADO_MS = 15_000;
const CURTO_MS = 2_000;

/** Quanto tempo uma leitura vale. Perto de uma escrita, quase nada. */
const validade = (): number =>
  Date.now() - ultimaEscrita < DESCONFIADO_MS ? CURTO_MS : FRESCO_MS;

/** Chamada depois de toda escrita: o próximo GET busca de novo. */
export const invalidar = (): void => {
  ultimaEscrita = Date.now();
  cache.clear();
  emVoo.clear();
};

function buscar<T>(path: string, init: RequestInit): Promise<T> {
  const p = (async () => {
    const res = await fetch(`${GATEWAY}/${path}`, {
      ...init,
      // O GitHub responde `Cache-Control: private, max-age=60`. Sem isto o
      // navegador servia a lista de 60 s atrás do cache HTTP: você apagava um
      // goal, o DELETE saía, o GitHub apagava — e a tela continuava mostrando o
      // item, inclusive depois de F5. O cache aqui em cima é nosso e nós
      // sabemos invalidar; o do navegador não obedece a nada disso.
      cache: "no-store",
      headers: { "content-type": "application/json", ...init.headers },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const source = path.split("/")[0];
      throw new SourceError(
        source,
        motivo(body, res.status),
        body.next ?? `verifique se ${source} está configurado e de pé`,
      );
    }
    return body as T;
  })();
  emVoo.set(path, p);
  return p.finally(() => emVoo.delete(path));
}

export async function gateway<T>(path: string, init: RequestInit = {}): Promise<T> {
  if ((init.method ?? "GET").toUpperCase() !== "GET") {
    return buscar<T>(path, init).finally(invalidar);
  }

  const guardado = cache.get(path);
  if (guardado) {
    // Vencido: devolve o que temos e revalida atrás. Se mudou, avisa o router.
    if (Date.now() - guardado.em >= validade() && !emVoo.has(path)) {
      void buscar<T>(path, init)
        .then((novo) => {
          const mudou = JSON.stringify(novo) !== JSON.stringify(guardado.dado);
          cache.set(path, { em: Date.now(), dado: novo });
          if (mudou) for (const f of ouvintes) f();
        })
        .catch(() => {});
    }
    return guardado.dado as T;
  }

  const voando = emVoo.get(path);
  if (voando) return voando as Promise<T>;

  const dado = await buscar<T>(path, init);
  cache.set(path, { em: Date.now(), dado });
  return dado;
}

// -------------------------------------------------------------------- ondas
//
// Onda é um lote que roda junto. Quem decide o que entra em cada uma é o
// orquestrador — ele lê as tasks e julga se divergem, que é raciocínio, não
// casamento de prefixo de caminho. Aqui só lemos o que ele registrou.

export interface WavePlan {
  number: number;
  state: string;
  tasks: Array<{ id: string; title: string; scope: string[]; role: string; state: string }>;
}

export const hope = {
  /** As ondas como o orquestrador as deixou. Task sem onda cai numa entrada
   *  própria no fim — ainda não foi despachada por ninguém. */
  async waves(project: string): Promise<WavePlan[]> {
    const [{ data: ondas, error: e1 }, { data: tasks, error: e2 }] = await Promise.all([
      db.from("waves").select("id, number, state").eq("project_id", project).order("number"),
      db
        .from("tasks")
        .select("id, wave_id, title, scope, role, state")
        .eq("project_id", project)
        .order("created_at"),
    ]);

    const erro = e1 ?? e2;
    if (erro) throw new SourceError("Supabase", erro.message, "confira a sessão e o projeto");

    const porOnda = new Map<string | null, WavePlan["tasks"]>();
    for (const t of tasks ?? []) {
      const lista = porOnda.get(t.wave_id) ?? [];
      lista.push({
        id: t.id,
        title: t.title,
        scope: (t.scope ?? "").split(/[,\s]+/).filter(Boolean),
        role: t.role,
        state: t.state,
      });
      porOnda.set(t.wave_id, lista);
    }

    const plano: WavePlan[] = (ondas ?? []).map((w) => ({
      number: w.number,
      state: w.state,
      tasks: porOnda.get(w.id) ?? [],
    }));

    const soltas = porOnda.get(null) ?? [];
    if (soltas.length) plano.push({ number: 0, state: "sem onda", tasks: soltas });

    return plano;
  },

  /** Resumo do que está acontecendo. Derivado do mesmo plano — não há uma
   *  segunda contagem que possa divergir dele. */
  async overview(): Promise<Overview> {
    const [plano, { data: tasks }, { data: msgs }] = await Promise.all([
      hope.waves(PROJETO),
      db.from("tasks").select("state"),
      db.from("messages").select("answered_at").is("answered_at", null),
    ]);

    return {
      ondas: plano.length,
      maior_paralelismo: Math.max(0, ...plano.map((w) => w.tasks.length)),
      tasks: tasks?.length ?? 0,
      rodando: (tasks ?? []).filter((t) => t.state === "rodando").length,
      perguntas_abertas: msgs?.length ?? 0,
    };
  },

  /** Cria uma task. Escrita direta no PostgREST — o RLS é a autorização, e
   *  não há endpoint nosso no meio para reimplementar o que ele já faz. */
  async createTask(project: string, title: string, scope: string, role = "executor") {
    if (!title.trim()) {
      throw new SourceError("hope", "toda task precisa de título", "descreva o trabalho em uma frase");
    }
    const { data, error } = await db
      .from("tasks")
      .insert({ project_id: project, title, scope, role })
      .select("id")
      .single();

    if (error) throw new SourceError("Supabase", error.message, "confira o projeto e a sessão");

    return {
      id: data.id as string,
      aviso: scope.trim() ? null : "sem escopo declarado: o orquestrador não tem como julgar se ela colide",
    };
  },
};

/** Projeto corrente enquanto o seletor não existe. */
export const PROJETO = "11111111-1111-1111-1111-111111111111";

export interface Overview {
  ondas: number;
  maior_paralelismo: number;
  tasks: number;
  rodando: number;
  perguntas_abertas: number;
}

// ------------------------------------------------------------------ LiteLLM

/** Forma real do `GET /v1/agents`, lida do openapi.json do LiteLLM — o modelo
 *  e a descrição vivem dentro de `agent_card_params`, junto com as skills e o
 *  agent card A2A que o gateway publica em
 *  `/a2a/{agent_id}/.well-known/agent-card.json`. */
export interface Agent {
  agent_id: string;
  agent_name: string;
  /** Identidade A2A: o card publicado em
   *  `/a2a/{agent_id}/.well-known/agent-card.json`. Não carrega modelo. */
  agent_card_params?: {
    name?: string;
    description?: string;
    version?: string;
    skills?: Array<{ id: string; name: string }>;
  };
  /** Roteamento: é aqui que o modelo vive, não no card. */
  litellm_params?: { model?: string };
}

/** Uma skill registrada no LiteLLM — o registro de skills do Claude Code que
 *  ele serve em `/claude-code/marketplace.json`. NÃO é a Skills API da
 *  Anthropic (`/v1/skills`): aquela é passthrough e exige ANTHROPIC_API_KEY,
 *  esta é o gateway falando de si mesmo, e é onde as 414 skills vivem. */
export interface Skill {
  id: string;
  name: string;
  version?: string;
  description?: string;
  category?: string;
  domain?: string;
  namespace?: string;
  enabled?: boolean;
  keywords?: string[];
  author?: { name?: string | null; email?: string | null } | null;
  source?: { source?: string; url?: string; path?: string; ref?: string } | null;
  created_at?: string;
  updated_at?: string;
}

/** Uma linha do agregado diário: `GET /spend/logs?start_date=&end_date=`.
 *  Além dos campos fixos, o LiteLLM devolve uma chave por hash de API key com
 *  o gasto daquele dia — por isso o índice aberto. */
export interface SpendDay {
  startTime: string;
  spend: number;
  users: Record<string, number>;
  models: Record<string, number>;
  [hashDaChave: string]: unknown;
}

/** Uma linha do log cru: `GET /spend/logs?api_key=`. Só os campos que as telas
 *  leem — o payload real traz `messages`, `response` e `metadata` inteiros. */
export interface SpendLog {
  api_key: string;
  session_id?: string | null;
  team_id?: string | null;
  model?: string | null;
  spend?: number | null;
  startTime?: string | null;
  total_tokens?: number | null;
  prompt_tokens?: number | null;
  cache_read_input_tokens?: number | null;
  metadata?: Record<string, unknown> | null;
}

/** Uma linha do catálogo `GET /v1/mcp/discover`. */
export interface McpCatalogoItem {
  name: string;
  title?: string;
  description?: string;
  icon_url?: string;
  category?: string;
  url?: string;
}

export interface McpServer {
  server_id?: string;
  alias?: string;
  server_name?: string;
  url?: string;
  transport?: string;
}

export const litellm = {
  agents: () =>
    gateway<{ data?: Agent[] } | Agent[]>("litellm/v1/agents").then((r) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),

  skills: () =>
    gateway<{ plugins?: Skill[]; count?: number }>("litellm/claude-code/plugins").then(
      (r) => r.plugins ?? [],
    ),

  /** Liga/desliga a skill para a frota. O endpoint casa por nome, não por id. */
  skillToggle: (nome: string, ligar: boolean) =>
    gateway(
      `litellm/claude-code/plugins/${encodeURIComponent(nome)}/${ligar ? "enable" : "disable"}`,
      { method: "POST" },
    ),

  skillDelete: (nome: string) =>
    gateway(`litellm/claude-code/plugins/${encodeURIComponent(nome)}`, { method: "DELETE" }),

  /** Catálogo de MCP do próprio gateway. É de onde sai o ícone: o LiteLLM
   *  publica `icon_url` por servidor conhecido, apontando para o CDN do
   *  Simple Icons. Não baixamos nem embutimos logo nenhum — a fonte é o
   *  gateway, e o CDN é o que ele mesmo escolheu. */
  mcpCatalogo: () =>
    gateway<{ servers?: McpCatalogoItem[] }>("litellm/v1/mcp/discover").then(
      (r) => r.servers ?? [],
    ),

  mcpServers: () =>
    gateway<{ data?: McpServer[] } | McpServer[]>("litellm/v1/mcp/server").then((r) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),

  models: () =>
    gateway<{ data?: Array<{ id: string }> }>("litellm/v1/models").then((r) => r.data ?? []),

  /** Gasto por dia. `start_date`/`end_date` mudam a FORMA da resposta: com
   *  janela o LiteLLM devolve um agregado diário (3 linhas, 2 KB); sem janela,
   *  o log cru inteiro — medido em 14.452 linhas e 81 MB, dos quais 61% é
   *  `metadata` e 32% são os prompts e respostas completos. Era a lentidão do
   *  app: quatro telas pediam o histórico de conversas para desenhar um total.
   *  `summarize=true` não ajuda — devolve os mesmos 81 MB. */
  spendByDay: (dias = 7): Promise<SpendDay[]> => {
    const dia = (d: Date) => d.toISOString().slice(0, 10);
    const fim = new Date();
    const inicio = new Date(fim.getTime() - (dias - 1) * 86_400_000);
    return gateway<SpendDay[]>(
      `litellm/spend/logs?start_date=${dia(inicio)}&end_date=${dia(fim)}`,
    );
  },

  /** Log cru de UMA chave. É o único jeito honesto de ver execução por task:
   *  a chave é etiquetada por task, então o filtro devolve dezenas de linhas em
   *  vez do banco inteiro. Nunca chame isto sem `api_key`. */
  spendForKey: (apiKey: string): Promise<SpendLog[]> =>
    gateway<SpendLog[]>(`litellm/spend/logs?api_key=${encodeURIComponent(apiKey)}`),

  /** Chaves e times: a multi-tenancy inteira, incluindo teto de gasto. */
  teams: () =>
    gateway<Array<{ team_id?: string; team_alias?: string; max_budget?: number; models?: string[] }>>(
      "litellm/team/list",
    ),
};

// ------------------------------------------------------------------- Linear

export interface Issue {
  id: string;
  identifier: string;
  title: string;
  url: string;
  state: { name: string; type: string };
  assignee?: { name: string } | null;
}

export interface Project {
  id: string;
  name: string;
  state: string;
  progress: number;
  url: string;
}

/** Linear, pelo MCP do LiteLLM — sem chave própria no app.
 *
 *  O desenho é o do Rafael: credencial mora num lugar só. O LiteLLM autentica
 *  o Linear uma vez (OAuth do servidor `linear_oficial`) e todo cliente herda —
 *  Claude, Codex e este app. Aqui não existe LINEAR_KEY.
 *
 *  A ponte é `POST /mcp-rest/tools/call` com `{server_id, name, arguments}`,
 *  contrato confirmado contra o gateway. A resposta do MCP vem como blocos de
 *  conteúdo; o texto do primeiro bloco é o JSON da ferramenta. */
const LINEAR_SERVER_ID = "894f1a39-3b94-4f2c-8435-4a1c09d4d664";

interface McpContent {
  content?: Array<{ type: string; text?: string }>;
  isError?: boolean;
}

async function mcp<T>(nome: string, args: Record<string, unknown> = {}): Promise<T> {
  const r = await gateway<McpContent>("litellm/mcp-rest/tools/call", {
    method: "POST",
    body: JSON.stringify({ server_id: LINEAR_SERVER_ID, name: nome, arguments: args }),
  });
  const texto = r.content?.find((c) => c.type === "text")?.text ?? "";
  if (r.isError) {
    throw new SourceError("Linear", texto.slice(0, 200) || "erro do MCP", "confira a conexão do servidor no LiteLLM");
  }
  try {
    return JSON.parse(texto) as T;
  } catch {
    throw new SourceError("Linear", "resposta do MCP não é JSON", "a ferramenta pode ter mudado de formato");
  }
}

/** Uma issue do Linear como o MCP devolve. */
export interface LinearIssue {
  id: string;
  title: string;
  description?: string;
  status?: string;
  statusType?: string;
  assignee?: string | null;
  project?: string | null;
  labels?: string[];
  url?: string;
  priority?: { value: number; name: string } | null;
  estimate?: number | null;
  team?: string;
  parentId?: string | null;
  /** Preenchido quando a issue foi arquivada — o board não a mostra. */
  createdAt?: string;
  updatedAt?: string;
}

export interface LinearStatus {
  id: string;
  type: string;
  name: string;
}

export const linear = {
  /** Status do time — é o que define as colunas do board. O time `bymav` tem
   *  Triage, Backlog, Todo, In Progress, In Review, Done, Canceled, Duplicate. */
  statuses: (team = "bymav") => mcp<LinearStatus[]>("list_issue_statuses", { team }),

  issues: (limite = 100) =>
    mcp<{ issues: LinearIssue[] }>("list_issues", {
      limit: limite,
      // O padrão da ferramenta é `true`, e o arquivo de um time do Linear guarda
      // arquivadas E deletadas no mesmo lugar (as deletadas ficam 30 dias antes
      // de sumirem de vez). Era daí que vinha um board cheio de issue que o
      // Rafael já tinha apagado. `false` filtra na fonte: com `true` vêm 60,
      // com `false` vem 0 — as 60 estão todas no lixo, nenhuma viva sobrou.
      includeArchived: false,
      fields: [
        "title", "status", "statusType", "assignee", "project", "labels",
        "url", "priority", "estimate", "updatedAt", "parentId", "team",
      ],
    }).then((r) => r.issues ?? []),

  issue: (id: string) =>
    mcp<LinearIssue>("get_issue", { id }),

  comments: (issueId: string) =>
    mcp<{ comments: Array<{ id: string; body: string; user?: string; createdAt?: string }> }>(
      "list_comments",
      { issueId },
    ).then((r) => r.comments ?? []),

  projects: () =>
    mcp<{ projects: Array<{ id: string; name: string; status?: string; url?: string }> }>(
      "list_projects",
      { limit: 50 },
    ).then((r) => r.projects ?? []),

  /** Cria ou atualiza. `save_issue` sem `id` cria; com `id`, edita — é assim
   *  que o MCP do Linear expõe as duas operações. */
  salvaIssue: (campos: Record<string, unknown>) => mcp<LinearIssue>("save_issue", campos),

  comenta: (issueId: string, body: string) =>
    mcp<unknown>("save_comment", { issueId, body }),
};

// ------------------------------------------------------------------- GitHub

export interface Repo {
  name: string;
  full_name: string;
  html_url: string;
  default_branch: string;
  updated_at: string;
}

/** Forma do `GET /repos/{repo}/issues`. `sub_issues_summary` e
 *  `issue_dependencies_summary` são nativos — é onde a onda vive. */
export interface GhIssue {
  number: number;
  title: string;
  html_url: string;
  state: "open" | "closed";
  labels: Array<{ name: string }>;
  created_at: string;
  updated_at: string;
  pull_request?: unknown;
  sub_issues_summary?: { total: number; completed: number };
  issue_dependencies_summary?: { blocked_by: number; blocking: number };
}

export interface GhComment {
  id: number;
  body: string;
  html_url: string;
  created_at: string;
  user: { login: string; avatar_url: string };
  /** `.../repos/o/r/issues/4` — o número sai daqui; a API não manda separado. */
  issue_url: string;
}

export interface Pull {
  number: number;
  title: string;
  html_url: string;
  draft: boolean;
  user: { login: string };
}

export interface Entry {
  name: string;
  path: string;
  type: "file" | "dir";
  size: number;
  html_url: string;
}

export const github = {
  repos: () => gateway<Repo[]>("github/user/repos?sort=updated&per_page=30"),
  repo: (full: string) => gateway<Repo>(`github/repos/${full}`),

  /** As tasks. Issue do GitHub é o dono: título, estado, dependência e
   *  sub-issue são dele, e o PR se liga sozinho quando o agente abre. */
  issues: (repo: string) =>
    gateway<GhIssue[]>(`github/repos/${repo}/issues?state=all&per_page=100`).then((xs) =>
      // A API devolve PR junto com issue no mesmo endpoint; PR tem página própria.
      xs.filter((x) => !x.pull_request),
    ),
  pulls: (repo: string) => gateway<Pull[]>(`github/repos/${repo}/pulls?state=open`),

  /** Conteúdo de um diretório do repo — serve tanto para Arquivos quanto para
   *  Conhecimento, porque a wiki vive versionada junto com o código. */
  contents: (repo: string, path = "") =>
    gateway<Entry[]>(`github/repos/${repo}/contents/${path}`),

  /** Comentários do repo inteiro, mais recentes primeiro. É a caixa de
   *  entrada: o agente pergunta no comentário da issue e você responde ali. */
  comments: (repo: string) =>
    gateway<GhComment[]>(
      `github/repos/${repo}/issues/comments?sort=created&direction=desc&per_page=50`,
    ),

  /** Abre uma task. Task é issue, então criar é POST /issues — o escopo vira
   *  label, que é como o GitHub guarda atributo de issue. */
  async createIssue(repo: string, title: string, scope: string, role: string) {
    if (!title.trim()) {
      throw new SourceError("hope", "toda task precisa de título", "descreva o trabalho em uma frase");
    }
    // Label é identificador, não texto de tela: "Senior Dev" vira "senior-dev".
    // Sem isso o mesmo papel gera duas labels e o board não agrupa.
    const papel = role.trim().toLowerCase().replace(/\s+/g, "-");
    const labels = [
      "estado:todo",
      ...(papel ? [`papel:${papel}`] : []),
      ...(scope.trim() ? [`escopo:${scope.trim()}`] : []),
    ];
    return gateway<GhIssue>(`github/repos/${repo}/issues`, {
      method: "POST",
      body: JSON.stringify({ title, labels }),
    });
  },

  /** Responder é comentar na issue. Não há caixa de saída nossa. */
  comment: (repo: string, numero: string, body: string) =>
    gateway<GhComment>(`github/repos/${repo}/issues/${numero}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  /** Quem somos, para saber qual comentário é seu e qual espera resposta. */
  me: () => gateway<{ login: string }>("github/user"),

  /** Últimos commits que tocaram um caminho. É daqui que sai a coluna
   *  "Modified" — a API de conteúdo não devolve data nenhuma. */
  commits: (repo: string, path: string, n = 1) =>
    gateway<Array<{ commit: { committer: { date: string } } }>>(
      `github/repos/${repo}/commits?path=${encodeURIComponent(path)}&per_page=${n}`,
    ),
};

// ---------------------------------------------------------------- nosso estado

export interface Task {
  id: string;
  title: string;
  scope: string | null;
  role: string;
  state: string;
  wave_id: string | null;
  pr_url: string | null;
  cost_cents: number;
}

export interface Wave {
  id: string;
  number: number;
  state: string;
  base_commit: string | null;
}

export interface Message {
  id: string;
  kind: string;
  body: string;
  options: string[] | null;
  answer: string | null;
  answered_at: string | null;
  created_at: string;
}

export interface Session {
  id: string;
  task_id: string;
  role: string;
  started_at: string;
  ended_at: string | null;
  cost_cents: number;
  tokens: number;
  result: string | null;
}

function guard() {
  if (!configured) throw naoConfigurado("Supabase");
}

export const state = {
  waves: () => (guard(), db.from("waves").select("*").order("number")),
  tasks: () => (guard(), db.from("tasks").select("*").order("created_at", { ascending: false })),
  sessions: () =>
    (guard(), db.from("sessions").select("*").order("started_at", { ascending: false }).limit(50)),
  messages: () =>
    (guard(), db.from("messages").select("*").order("created_at", { ascending: false }).limit(50)),

  answer: (id: string, answer: string) =>
    db.from("messages").update({ answer, answered_at: new Date().toISOString() }).eq("id", id),
};
